/**
 * RapidAPI Key Management with Per-Host Failure Tracking + Optional Fallback
 *
 * The RapidAPI key is env-driven:
 *   - RAPIDAPI_KEY           (REQUIRED) — primary key, server-only (no NEXT_PUBLIC_).
 *   - RAPIDAPI_KEY_FALLBACK  (OPTIONAL) — a second key for real failover. When
 *                             unset, the module degrades cleanly to single-key.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │              HOST SUBSCRIPTION MATRIX (current RAPIDAPI_KEY)           │
 * │                                                                        │
 * │  starapi1.p.rapidapi.com                │  ✅ subscribed              │
 * │  flashapi1.p.rapidapi.com               │  ✅ subscribed              │
 * │  tiktok-best-experience.p.rapidapi.com  │  ❌ NOT subscribed (403)    │
 * │  tiktok-scraper7.p.rapidapi.com         │  ❌ NOT subscribed (403)    │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * HOW IT WORKS:
 *  1. resolveKeyChain() builds an ordered, de-duplicated key list from env:
 *     [RAPIDAPI_KEY] or [RAPIDAPI_KEY, RAPIDAPI_KEY_FALLBACK].
 *  2. makeRapidApiRequest() tries the keys in order for the given host.
 *  3. On a key-level failure (401/403 + RapidAPI error patterns) it marks that
 *     key failed FOR THIS HOST ONLY and falls through to the next key (if any).
 *  4. Failure state is tracked PER-HOST — a key failing on FlashAPI does NOT
 *     affect StarAPI requests (this was the bug with the old global system).
 *  5. Failed keys auto-recover after 30 min in case the issue was transient.
 *  6. A missing RAPIDAPI_KEY throws loudly at request time (names the env var) —
 *     never a silent 403 loop or unbounded hang.
 *
 * TO ADD A SECOND KEY (redundancy): set RAPIDAPI_KEY_FALLBACK in the server env.
 * No code change needed.
 *
 * Usage in providers:
 *   import { makeRapidApiRequest } from '@shared-libs/rapidapi-keys'
 *   const data = await makeRapidApiRequest(url, options, host, "ProviderName")
 */

import { Notifications } from "./logsnag"

// ─── Keys (env-driven) ─────────────────────────────────────────────────────────
// Server-only (no NEXT_PUBLIC_ prefix). RAPIDAPI_KEY is required;
// RAPIDAPI_KEY_FALLBACK is optional — when unset the module runs single-key.
const RAPIDAPI_PRIMARY_KEY = process.env.RAPIDAPI_KEY
const RAPIDAPI_FALLBACK_KEY = process.env.RAPIDAPI_KEY_FALLBACK

// Legacy named exports — consumed only by the barrel re-exports in
// instagram/index.ts and tiktok/index.ts (a few client components import the
// Instagram class / string helpers through those barrels). They must stay plain
// string exports and must NEVER carry the key into the browser bundle, so they
// read from env (→ "" on the client). NOT used for any API call — all calls go
// through makeRapidApiRequest().
export const PRIMARY_RAPIDAPI_KEY = RAPIDAPI_PRIMARY_KEY ?? ""
export const FALLBACK_RAPIDAPI_KEY = RAPIDAPI_FALLBACK_KEY ?? RAPIDAPI_PRIMARY_KEY ?? ""

// ─── Key Resolution ────────────────────────────────────────────────────────────
// Ordered, de-duplicated key chain: [primary] or [primary, fallback]. Throws
// loudly (naming the env var) when the required primary key is missing. Runs at
// REQUEST time, not module load, so importing the barrel from a client component
// never crashes at import; a genuinely missing key fails fast instead of looping
// on silent 403s.
function resolveKeyChain(): string[] {
    if (!RAPIDAPI_PRIMARY_KEY) {
        throw new Error(
            "[RapidAPI Keys] Missing required env var RAPIDAPI_KEY. Set it in the server environment (.env.local locally, Vercel project env in prod). RapidAPI requests cannot proceed."
        )
    }
    const chain = [RAPIDAPI_PRIMARY_KEY]
    if (RAPIDAPI_FALLBACK_KEY && RAPIDAPI_FALLBACK_KEY !== RAPIDAPI_PRIMARY_KEY) {
        chain.push(RAPIDAPI_FALLBACK_KEY)
    }
    return chain
}

// Per-host key order. Every known host currently shares the same env key chain;
// this seam is kept so a future distinct key can be routed to specific hosts by
// returning an override here. The valuable per-host machinery is the FAILURE
// state below, which is independent of the key values.
function getKeysForHost(_host: string): string[] {
    return resolveKeyChain()
}

// ─── Per-Host Failure State ──────────────────────────────────────────────────
interface HostKeyState {
    failedKeys: Map<string, Date> // key value → time it was marked failed
    alertSent: boolean
}

const hostStates = new Map<string, HostKeyState>()

// Auto-recover: re-check failed keys every 30 minutes
const KEY_RECOVERY_INTERVAL_MS = 30 * 60 * 1000

function getHostState(host: string): HostKeyState {
    if (!hostStates.has(host)) {
        hostStates.set(host, { failedKeys: new Map(), alertSent: false })
    }
    return hostStates.get(host)!
}

function checkHostKeyRecovery(host: string): void {
    const state = getHostState(host)
    for (const [key, failedAt] of state.failedKeys) {
        const elapsed = Date.now() - failedAt.getTime()
        if (elapsed > KEY_RECOVERY_INTERVAL_MS) {
            console.log(`[RapidAPI Keys] Recovery window reached for host ${host} key ...${key.slice(-4)} (${Math.round(elapsed / 60000)}min). Re-enabling.`)
            state.failedKeys.delete(key)
            state.alertSent = false
        }
    }
}

function markHostKeyFailed(
    host: string,
    key: string,
    context: { provider: string; endpoint: string; statusCode: number; responseBody: string }
): void {
    const state = getHostState(host)
    if (state.failedKeys.has(key)) return // already marked

    state.failedKeys.set(key, new Date())
    const keys = getKeysForHost(host)
    const preferredKey = keys[0]
    const isPreferred = key === preferredKey
    const hasFallback = keys.length > 1

    console.error(`[RapidAPI Keys] Key ...${key.slice(-4)} marked FAILED for host ${host}`, context)

    if (isPreferred && hasFallback && !state.alertSent) {
        // Preferred key failed — switched to fallback (only when a REAL distinct fallback exists)
        state.alertSent = true
        Notifications.sendNotification(
            "api-alerts",
            `RapidAPI Key Failed for ${context.provider} — Switched to Fallback`,
            "🚨",
            true,
            undefined,
            {
                provider: context.provider,
                endpoint: context.endpoint,
                "status-code": context.statusCode,
                "response-preview": context.responseBody.substring(0, 200),
                "failed-at": new Date().toISOString(),
                "failed-key-last-four": key.slice(-4),
                "fallback-key-last-four": keys[1].slice(-4),
                host
            }
        ).catch((err) => console.error("[RapidAPI Keys] Failed to send LogSnag alert:", err))
    }

    // All keys for this host are now failed → nothing left to try.
    if (keys.every((k) => state.failedKeys.has(k))) {
        Notifications.sendNotification(
            "api-alerts",
            `CRITICAL: All Keys Failed for ${context.provider}`,
            "🔴",
            true,
            undefined,
            {
                provider: context.provider,
                endpoint: context.endpoint,
                "status-code": context.statusCode,
                "response-preview": context.responseBody.substring(0, 200),
                "failed-at": new Date().toISOString(),
                host
            }
        ).catch((err) => console.error("[RapidAPI Keys] Failed to send critical LogSnag alert:", err))
    }
}

// ─── Error Detection ─────────────────────────────────────────────────────────

/**
 * Transient rate-limit / quota-window signals. A 429 is NOT a dead key — it is a
 * throttle that clears on its own. It must NEVER evict the key or trip the 30-min
 * recovery timer: with a single key that would empty the try-list and manufacture
 * a self-inflicted 30-min outage under signup step-0 from one transient blip.
 */
const RATE_LIMIT_PATTERNS = [
    "too many requests",
    "exceeded the rate limit",
    "quota exceeded",
    "exceeded the monthly quota",
    "exceeded the daily quota"
]

/**
 * Genuine key-death signals — the key itself is unusable (auth / subscription /
 * suspension). Only these evict the key (and switch to a fallback if configured).
 * Kept distinct from RATE_LIMIT_PATTERNS so a throttle is never mistaken for death.
 */
const KEY_FAILURE_PATTERNS = [
    "you are not subscribed",
    "account suspended",
    "subscription has been suspended",
    "invalid api key",
    "not allowed to access this api",
    "blocked user"
]

function isRateLimited(status: number, body: string): boolean {
    const lower = body.toLowerCase()
    if (RATE_LIMIT_PATTERNS.some((pattern) => lower.includes(pattern))) return true
    if (status === 429) return true
    return false
}

function isKeyFailure(status: number, body: string): boolean {
    const lower = body.toLowerCase()
    if (KEY_FAILURE_PATTERNS.some((pattern) => lower.includes(pattern))) return true
    if (status === 401) return true
    return false
}

// ─── Main Request Function ───────────────────────────────────────────────────

// Per-attempt hard timeout. The provider chain only advances on a thrown error, so
// without this a wedged socket (RapidAPI held one for minutes on a lapsed sub, July
// 2026) hangs the whole request into a platform 504. 3.5s lets the rotation still
// reach a healthy fallback within the caller's overall deadline.
// 2026-09-20: raised 3_500 → 8_000. StarAPI's healthy responses now take 1.8–3.5s, so the
// 3.5s abort cut off real answers (33 timeouts/hour on 09-20 with the provider returning 200s
// on direct probes). With a single configured key there is no second attempt, so the abort
// surfaced as a failed search/profile lookup for the customer.
const ATTEMPT_TIMEOUT_MS = 8_000

export interface RapidApiQuotaSnapshot {
    requestsLimit: number | null
    requestsRemaining: number | null
    requestsResetSeconds: number | null
    freePlanHardLimit: number | null
    freePlanHardRemaining: number | null
    freePlanHardResetSeconds: number | null
}

export interface RapidApiRequestControls {
    maxAttempts?: number
    logQuota?: boolean
}

function integerHeader(headers: Pick<Headers, "get">, name: string): number | null {
    const value = headers.get(name)
    if (value === null || !/^\d+$/.test(value.trim())) return null
    const parsed = Number(value)
    return Number.isSafeInteger(parsed) ? parsed : null
}

export function readRapidApiQuotaHeaders(headers: Pick<Headers, "get">): RapidApiQuotaSnapshot | null {
    const snapshot: RapidApiQuotaSnapshot = {
        requestsLimit: integerHeader(headers, "x-ratelimit-requests-limit"),
        requestsRemaining: integerHeader(headers, "x-ratelimit-requests-remaining"),
        requestsResetSeconds: integerHeader(headers, "x-ratelimit-requests-reset"),
        freePlanHardLimit: integerHeader(headers, "x-rate-limit-rapid-free-plans-hard-limit-limit"),
        freePlanHardRemaining: integerHeader(headers, "x-rate-limit-rapid-free-plans-hard-limit-remaining"),
        freePlanHardResetSeconds: integerHeader(headers, "x-rate-limit-rapid-free-plans-hard-limit-reset")
    }

    return Object.values(snapshot).some((value) => value !== null) ? snapshot : null
}

function logRapidApiQuota(
    response: Pick<Response, "headers" | "status">,
    provider: string,
    host: string,
    url: string
): void {
    const quota = readRapidApiQuotaHeaders(response.headers)
    if (!quota) return

    console.info("[RapidAPI Quota]", {
        provider,
        host,
        endpoint: new URL(url).pathname,
        statusCode: response.status,
        observedAt: new Date().toISOString(),
        ...quota
    })
}

// Hosts known to be unsubscribed on the current key(s). Fail fast + QUIETLY here
// instead of hammering a guaranteed-403 (RapidAPI quota is POOLED with UpGrow prod)
// and firing a CRITICAL page on every call (alert fatigue buries the next real one).
// Re-subscribe → remove the host from this set.
//
// 2026-07-28: `tiktok-best-experience` was RE-SUBSCRIBED and removed from this set —
// verified live on the real provider paths (`/user/tiktok` → uid 107955 "TikTok";
// `/user/id/<id>` → 200). The other two remain 403 "not subscribed", so the TikTok
// PRIMARY is live while BOTH its fallbacks are dead: a single upstream failure there
// has nowhere to rotate to. Re-check before relying on TikTok redundancy.
const UNSUBSCRIBED_HOSTS = new Set<string>(["tiktok-scraper7.p.rapidapi.com", "tiktok82.p.rapidapi.com"])

/**
 * Makes a RapidAPI request with per-host automatic key fallback.
 *
 * 1. Gets the key order for this host (based on subscription status)
 * 2. Skips keys marked as failed for this host (unless recovery window passed)
 * 3. Tries remaining keys in order
 * 4. On key-level failure, marks that key failed FOR THIS HOST ONLY
 */
export async function makeRapidApiRequest(
    url: string,
    options: RequestInit & { headers?: Record<string, string> },
    host: string,
    provider: string = "unknown",
    controls: RapidApiRequestControls = {}
): Promise<any> {
    // Known-unsubscribed host → fail fast and QUIETLY (no API call, no key eviction,
    // no CRITICAL page). The provider failover chain treats this as a normal miss.
    if (UNSUBSCRIBED_HOSTS.has(host)) {
        throw new Error(`[RapidAPI Keys] Host ${host} is not subscribed on the current key — skipping ${provider} (fast-fail, no alert).`)
    }

    checkHostKeyRecovery(host)

    // Throws loudly if RAPIDAPI_KEY is unset (names the env var) — no silent loop.
    const keys = getKeysForHost(host)
    const state = getHostState(host)

    // Ordered list of keys to try, skipping any already marked failed for this host.
    // The chain is already de-duplicated, so single-key mode never retries the same key.
    const eligibleKeys: Array<{ key: string; label: string }> = keys
        .map((k, i) => ({ key: k, label: `${i === 0 ? "preferred" : "fallback"}(...${k.slice(-4)})` }))
        .filter((entry) => !state.failedKeys.has(entry.key))
    const requestedAttempts =
        Number.isSafeInteger(controls.maxAttempts) && Number(controls.maxAttempts) > 0
            ? Number(controls.maxAttempts)
            : eligibleKeys.length
    const keysToTry = eligibleKeys.slice(0, requestedAttempts)

    if (keysToTry.length === 0) {
        throw new Error(`[RapidAPI Keys] All keys failed for ${provider} (${host}). Auto-recovery in ${KEY_RECOVERY_INTERVAL_MS / 60000} min.`)
    }

    let lastError: Error | null = null

    for (const { key, label } of keysToTry) {
        try {
            const headers = {
                ...options.headers,
                "X-RapidAPI-Key": key,
                "X-RapidAPI-Host": host
            }

            const response = await fetch(url, { ...options, headers, signal: options.signal ?? AbortSignal.timeout(ATTEMPT_TIMEOUT_MS) })
            if (controls.logQuota) logRapidApiQuota(response, provider, host, url)
            const responseText = await response.text()

            // Transient throttle — surface the error but NEVER evict the key or trip the
            // 30-min recovery timer. Evicting a single key here manufactures a self-
            // inflicted 30-min outage; the key stays warm for the next request.
            if (isRateLimited(response.status, responseText)) {
                console.warn(`[RapidAPI Keys] Rate-limited (${label}) for ${provider} (${url}): HTTP ${response.status} — not evicting key`)
                lastError = new Error(`RapidAPI rate-limited: HTTP ${response.status} - ${responseText.substring(0, 200)}`)
                continue
            }

            if (isKeyFailure(response.status, responseText)) {
                console.warn(`[RapidAPI Keys] Key ${label} failed for ${provider} (${url}): HTTP ${response.status}`)
                markHostKeyFailed(host, key, {
                    provider,
                    endpoint: url,
                    statusCode: response.status,
                    responseBody: responseText
                })
                lastError = new Error(`RapidAPI key ${label} failed: HTTP ${response.status} - ${responseText.substring(0, 200)}`)
                continue
            }

            try {
                return JSON.parse(responseText)
            } catch {
                return responseText
            }
        } catch (error) {
            // Network errors — not key-specific
            console.error(`[RapidAPI Keys] Network error for ${provider} with ${label} key:`, error)
            lastError = error instanceof Error ? error : new Error(String(error))
        }
    }

    throw lastError || new Error(`All RapidAPI keys exhausted for ${provider}`)
}

// ─── Legacy exports ──────────────────────────────────────────────────────────
// These resolve to the env-driven primary key and exist solely because
// instagram/index.ts and tiktok/index.ts re-export them from their barrel files.
// No consumer imports them for calls. All actual API calls go through
// makeRapidApiRequest() above.
// DO NOT use these for API calls — they bypass per-host key routing.
export const STARAPI_KEY = PRIMARY_RAPIDAPI_KEY
export const RAPIDAPI_KEY = PRIMARY_RAPIDAPI_KEY
export const TIKTOK_RAPIDAPI_KEY = PRIMARY_RAPIDAPI_KEY

// Legacy functions — previously used for global key state. Now unused externally
// (all providers use makeRapidApiRequest) but kept to avoid breaking any
// lingering imports. These are intentional no-ops.
export function getActiveKey(): string {
    return PRIMARY_RAPIDAPI_KEY
}
export function getActiveKeyName(): "primary" | "fallback" {
    return "primary"
}
export function markKeyAsFailed(_key: "primary" | "fallback", _context?: any): void {}
